import { createContext, ReactNode, useCallback, useContext, useMemo } from "react";

export interface InsightMetaActions {
  saveDescription: (text: string) => Promise<void>;
  updateDescription: () => Promise<string | undefined>;
}

const InsightMetaContext = createContext<InsightMetaActions | undefined>(undefined);

export const useInsightMeta = () => {
  const context = useContext(InsightMetaContext);
  if (!context) {
    throw new Error("useInsightMeta must be used within an InsightMetaProvider");
  }
  return context;
};

interface InsightMetaProps {
  children: ReactNode;
  handleUpdateDescription: (text: string) => Promise<void>;
  handleRefreshDescription: () => Promise<string | undefined>;
}

export const InsightMetaProvider = ({
  children,
  handleUpdateDescription,
  handleRefreshDescription,
}: InsightMetaProps) => {
  const saveDescription = useCallback(
    (text: string) => handleUpdateDescription(text),
    [handleUpdateDescription]
  );

  const updateDescription = useCallback(
    () => handleRefreshDescription(),
    [handleRefreshDescription]
  );

  const value = useMemo<InsightMetaActions>(
    () => ({ saveDescription, updateDescription }),
    [saveDescription, updateDescription]
  );

  return (
    <InsightMetaContext.Provider value={value}>
      {children}
    </InsightMetaContext.Provider>
  );
};
